import { useMutation } from "@apollo/client";
import { getFormProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import type { ColorPicker } from "framework7/types";
import { Block, Button, List, ListInput } from "framework7-react";
import { useState } from "react";

import { UpsertUserFeelingDocument, UserFeelingFragment, UserFeelingInput } from "@/generated/graphql/graphql";
import { UserFeelingInputSchema } from "@/generated/graphql/graphql.zod";
import { useColorPalette } from "@/hooks/useColorPalette";

type FeelingFormProps = { userFeeling: UserFeelingFragment; onCompleted: VoidFunction };

export function FeelingForm({ userFeeling, onCompleted }: FeelingFormProps) {
  const [upsertUserFeeling, { loading }] = useMutation(UpsertUserFeelingDocument);

  const [form, fields] = useForm<UserFeelingInput>({
    defaultValue: {
      color: userFeeling.color,
      name: userFeeling.name,
    },
    onSubmit(event, { formData }) {
      event.preventDefault();

      const submission = parseWithZod(formData, { schema: UserFeelingInputSchema });
      if (submission.status !== "success") return submission.reply();

      return upsertUserFeeling({
        onCompleted,
        variables: {
          feelingKey: userFeeling.feelingKey,
          userFeelingInput: submission.value,
        },
      });
    },
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: UserFeelingInputSchema });
    },
  });

  const colorPalette = useColorPalette();
  const [color, setColor] = useState<string>(userFeeling.color);

  return (
    <form {...getFormProps(form)}>
      <input
        name={fields.color.name}
        type="hidden"
        value={color}
      />

      <List inset>
        <ListInput
          required
          validate
          defaultValue={fields.name.value}
          errorMessage={fields.name.errors?.join("\n")}
          label="気分名"
          name={fields.name.name}
          type="text"
        />

        <ListInput
          readonly
          colorPickerParams={{
            groupedModules: true,
            hexLabel: true,
            hexLabelText: "気分の色 (HEX)",
            hexValueEditable: true,
            modules: ["hex", "palette"],
            navbarBackLinkText: "戻る",
            navbarCloseText: "完了",
            navbarTitleText: "色を選択",
            openIn: "auto",
            openInPhone: "sheet",
            palette: colorPalette,
            sliderLabel: true,
            sliderValue: true,
            targetElSetBackgroundColor: false,
            toolbarCloseText: "完了",
            value: { hex: color },
          }}
          label="気分の色"
          placeholder="Color"
          type="colorpicker"
          onColorPickerChange={(v: ColorPicker.ColorPickerValue) => {
            if (v?.hex) setColor(v.hex);
          }}
        >
          <i
            className="icon"
            slot="media"
            style={{ backgroundColor: color, borderRadius: 6, height: 28, width: 28 }}
          />
        </ListInput>
      </List>

      <Block>
        <div
          className="grid grid-cols-2 grid-gap"
          style={{ padding: "var(--f7-safe-area-bottom)" }}
        >
          <Button
            outline
            disabled={loading}
            type="button"
          >
            キャンセル
          </Button>
          <Button
            fill
            loading={loading}
            type="submit"
          >
            保存する
          </Button>
        </div>
      </Block>
    </form>
  );
}
